import { useFormField } from "@/form/useForm/useFormField";
import { ChatAvatar } from "../chat-avatar";
import { defineField } from "@/form/useForm";
import TextArea from "components/textarea";
import { PROMPT_SUGGESTIONS } from "../../constants/promptSuggestions";
import { PromptPill } from "../prompt-pill";
import { ChatButton } from "../chat-button";
import Icon from "components/icon";
import { SendButton } from "../send-button";
import { useAssistant, type UseAssistant } from "~/composables/useAssistant";
import { MessageBubble } from "../message-bubble";
import Avatar from "components/avatar";
import { twColor } from "@/utils/style";
import { onKeyPressed } from "@vueuse/core";
import { EAssistantEvent } from "~/composables/useAssistant/types/message";
import { unwrapElementRef } from "@/utils/dom/unwrap";
import type { IPromptSuggestion } from "../../types/promptSuggestion";
import WithBubbletip from "components/with-bubbletip";

const InitScreen = defineComponent<{
  assistant: UseAssistant;
}>(
  (props) => {
    const handleClickSuggestion = (suggestion: IPromptSuggestion) => {
      props.assistant.sendMessage(suggestion.prompt || suggestion.label);
    };

    return () => {
      return (
        <div class="w-full h-full grid items-center justify-center">
          <div class="flex flex-col items-center gap-8">
            <ChatAvatar />
            <div class="flex flex-col items-center gap-2 text-center">
              <h1 class="font-['Inter'] text-base font-semibold tracking-[-0.025em] text-gray-900 leading-[1.1]">
                Hello, how can I help you today?
              </h1>
              <p class="text-sm text-gray-500 font-normal font-['Inter'] whitespace-nowrap">
                Ask questions about your portfolio or ask me to do something for you.
              </p>
            </div>
            <div class="flex flex-wrap justify-center gap-2 max-w-[640px]">
              {PROMPT_SUGGESTIONS.map((suggestion) => {
                return (
                  <PromptPill
                    onClick={() => handleClickSuggestion(suggestion)}
                    key={`${suggestion.label}`}
                  >
                    {suggestion.label}
                  </PromptPill>
                );
              })}
            </div>
          </div>
        </div>
      );
    };
  },
  { props: ["assistant"] },
);

const AgentAvatar = () => (
  <Avatar
    circle
    colorPreference={twColor("primary-900")}
    src={() => (
      <Icon
        size="1rem"
        fill={twColor("findex-primary-500")}
        src={async () => import("assets/img/svg/icons/ai-avatar.svg?raw")}
      />
    )}
  />
);

const MessageScreen = defineComponent<{
  assistant: UseAssistant;
}>(
  (props) => {
    return () => {
      return (
        <div class="w-full h-full min-w-0 flex flex-col">
          <div class="w-full min-w-0 flex flex-col gap-5">
            {props.assistant.messages.value.map((msg) => {
              return (
                <MessageBubble
                  image={msg.role === "agent" ? AgentAvatar : undefined}
                  text={msg.content}
                  key={`message-${msg.id}-${msg.thinking}`}
                  senderRole={msg.role}
                  chunk={msg.chunk}
                  thinking={msg.thinking}
                  stream={msg.stream}
                  toolCall={msg.toolCall}
                  attachments={msg.attachments}
                />
              );
            })}
          </div>
        </div>
      );
    };
  },
  { props: ["assistant"] },
);

const InputBar = defineComponent<{
  assistant: UseAssistant;
  onClickSend: () => void;
}>(
  (props) => {
    return () => {
      return (
        <div class="w-full h-10 px-3 bg-gray-100 border-t border-gray-300 grid grid-cols-[1fr_max-content] justify-between items-center">
          <div class="grid grid-cols-[repeat(auto-fit,minmax(max-content,1rem))] items-center gap-2">
            <WithBubbletip tooltip={{ variant: "white", text: "Coming Soon" }}>
              <ChatButton variant="default">
                <Icon
                  size="0.625rem"
                  fill="currentColor"
                  src={async () => import("assets/img/svg/icons/cog.svg?raw")}
                />
              </ChatButton>
            </WithBubbletip>
            <WithBubbletip
              tooltip={{
                variant: "white",
                text: "Coming Soon"
              }}
            >
              <ChatButton variant="default">
                <Icon
                  stroke="currentColor"
                  size="0.625rem"
                  src={async () =>
                    import("assets/img/svg/icons/rewind_time.svg?raw")
                  }
                />
              </ChatButton>
            </WithBubbletip>
            <WithBubbletip tooltip={{ variant: "white", text: "Coming Soon" }}>
              <ChatButton variant="default">
                <Icon
                  size="0.625rem"
                  fill="currentColor"
                  src={async () =>
                    import("assets/img/svg/icons/closed_book.svg?raw")
                  }
                />
              </ChatButton>
            </WithBubbletip>
            <WithBubbletip tooltip={{ variant: "white", text: "Attach files" }}>
              <span
                onClick={() => {
                  if (props.assistant.attachmentsUploading.value) return;
                  props.assistant.attachFiles();
                }}
                class={props.assistant.attachmentsUploading.value ? "opacity-50 pointer-events-none" : ""}
              >
                <ChatButton variant="default">
                  <Icon
                    size="0.625rem"
                    fill="currentColor"
                    src={async () =>
                      import("assets/img/svg/icons/paper-clip.svg?raw")
                    }
                  />
                </ChatButton>
              </span>
            </WithBubbletip>
            {props.assistant.attachments.value.length > 0 ? (
              <div class="ml-1 flex items-center gap-1 max-w-[320px] overflow-x-auto pretty-scrollbar">
                {props.assistant.attachments.value.map((a) => {
                  const truncated = a.name.length > 18
                    ? `${a.name.slice(0, 8)}…${a.name.slice(-7)}`
                    : a.name;
                  if (a.status === 'uploading') {
                    return (
                      <div
                        key={a.key}
                        title={`Uploading ${a.name}`}
                        class="inline-flex items-center gap-1 pl-1.5 pr-2 py-0.5 rounded-md bg-gray-50 text-gray-500 text-[10px] font-medium font-['Inter'] leading-none whitespace-nowrap border border-gray-200/80"
                      >
                        <span class="w-3 h-3 inline-flex items-center justify-center">
                          <span class="w-2.5 h-2.5 rounded-full border border-gray-300 border-t-primary-500 animate-spin" />
                        </span>
                        <span>{truncated}</span>
                      </div>
                    );
                  }
                  if (a.status === 'error') {
                    return (
                      <div
                        key={a.key}
                        title={a.errorMessage}
                        class="inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded-md bg-red-50 text-red-700 text-[10px] font-medium font-['Inter'] leading-none whitespace-nowrap border border-red-200/70"
                      >
                        <span class="w-3 h-3 inline-flex items-center justify-center text-red-500 text-[10px] leading-none">!</span>
                        <span>{truncated}</span>
                        <button
                          type="button"
                          onClick={() => { props.assistant.retryAttachment(a.key); }}
                          title="Retry upload"
                          class="ml-0.5 px-1 rounded text-red-600 hover:text-red-800 hover:bg-white/70 text-[10px] leading-none"
                        >
                          retry
                        </button>
                        <button
                          type="button"
                          onClick={() => { props.assistant.removeAttachment(a.key); }}
                          title={`Remove ${a.name}`}
                          class="ml-0.5 w-3 h-3 inline-flex items-center justify-center rounded-full text-red-400 hover:text-red-700 hover:bg-white/70 text-[10px] leading-none"
                        >
                          ×
                        </button>
                      </div>
                    );
                  }
                  return (
                    <button
                      key={a.key}
                      type="button"
                      onClick={() => { props.assistant.removeAttachment(a.key); }}
                      title={`Remove ${a.name}`}
                      class="group inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded-md bg-primary-50 hover:bg-primary-100 text-primary-700 text-[10px] font-medium font-['Inter'] leading-none whitespace-nowrap border border-primary-200/60"
                    >
                      <span class="w-3 h-3 inline-flex items-center justify-center text-primary-500">
                        <Icon
                          size="0.5rem"
                          fill="currentColor"
                          src={async () => import("assets/img/svg/icons/paper-clip.svg?raw")}
                        />
                      </span>
                      <span>{truncated}</span>
                      <span class="ml-0.5 w-3 h-3 inline-flex items-center justify-center rounded-full text-primary-400 group-hover:text-primary-600 group-hover:bg-white/70 text-[10px] leading-none">
                        ×
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
          <div class="grid grid-cols-[repeat(auto-fit,minmax(max-content,1rem))] items-center">
            <SendButton
              onClick={props.onClickSend}
              class="text-primary-500 hover:text-primary-600"
            />
          </div>
        </div>
      );
    };
  },
  { props: ["assistant", "onClickSend"] },
);

const InputArea = defineComponent<{
  assistant: UseAssistant;
}>(
  (props) => {
    const messageField = useFormField<string>({
      schema: defineField({
        type: "text",
        placeholder: "Write your message here...",
      }),
    });

    const handleSendMessage = () => {
      const message = messageField.dataRef.value.value;
      if (message && message.trim().length > 0) {
        props.assistant.sendMessage(message);
      }
      messageField.dataRef.setValue("");

      setTimeout(() => {
        messageField.dataRef.setValue("");
      }, 100);
    };

    onKeyPressed("Enter", (e) => {
      if (e.shiftKey) { return }
      handleSendMessage();
    });

    return () => {
      return (
        <div class="w-full">
          <div class="border border-gray-300 rounded-lg overflow-hidden [&_textarea:focus]:ring-0 [&_textarea:focus]:outline-none [&_textarea:focus]:border-transparent">
            <TextArea
              dataRef={messageField.dataRef}
              placeholder={messageField.dataRef.placeholder}
              style={{ border: "none", borderRadius: "0px" }}
              variant="gray"
              textSize="xs"
            />
            <InputBar
              onClickSend={handleSendMessage}
              assistant={props.assistant}
            />
          </div>
        </div>
      );
    };
  },
  { props: ["assistant"] },
);

const RecentChats = defineComponent(() => {
  return () => {
    return (
      <div class="w-full self-stretch py-4 inline-flex flex-col justify-start items-start gap-2 overflow-hidden">
        <div class="self-stretch inline-flex justify-between items-center">
          <div class="flex justify-start items-center gap-1">
            <div class="justify-center text-gray-500 text-xs font-semibold font-['Inter'] leading-none">
              Recent Chats
            </div>
            <WithBubbletip
              tooltip={{
                variant: "white",
                wrapText: true,
                body: () => {
                  return (
                    <div class="max-w-[258px]">
                      Ability to save historic chats is coming soon. Once
                      implemented, you will be able to have persistent chats
                      and extended memory for your AI Agent. This means that
                      the AI will be able to remember your portfolio
                      development and provide you with relevant insights and
                      your specific needs.
                    </div>
                  );
                },
              }}
            >
              <span class="text-gray-500">
                <Icon
                  fill="currentColor"
                  size="0.75rem"
                  src={async () =>
                    import("assets/img/svg/icons/info-circle.svg?raw")
                  }
                />
              </span>
            </WithBubbletip>
          </div>
          <div class="justify-center text-primary-500 text-xs font-medium font-['Inter'] leading-none">
            View All
          </div>
        </div>
        <div class="self-stretch inline-flex justify-between items-center">
          <div class="w-70 justify-center text-gray-500 text-[10px] font-normal font-['Inter'] leading-none">
            Ability to access historic chats coming soon
          </div>
          <div class="justify-center text-gray-500 text-[10px] font-normal font-['Inter'] leading-none flex-nowrap">
            Date for chat
          </div>
        </div>
      </div>
    );
  };
});

const DropOverlay = defineComponent<{ visible: boolean }>(
  (props) => {
    return () => {
      if (!props.visible) return null;
      return (
        <div
          class="absolute inset-0 z-50 pointer-events-none animate-[dropOverlayIn_180ms_ease-out_forwards]"
          aria-hidden="true"
        >
          <div class="absolute inset-0 bg-gradient-to-b from-white/95 via-primary-50/90 to-white/95 backdrop-blur-[10px]" />
          <div class="absolute inset-3 overflow-hidden rounded-[22px]">
            <svg
              class="absolute inset-0 w-full h-full"
              preserveAspectRatio="none"
              fill="none"
            >
              <rect
                x="1"
                y="1"
                width="calc(100% - 2px)"
                height="calc(100% - 2px)"
                rx="20"
                ry="20"
                stroke="currentColor"
                class="text-primary-400/70"
                stroke-width="1.5"
                stroke-dasharray="10 8"
                style="animation: dropDashMarch 1.6s linear infinite;"
              />
            </svg>
          </div>
          <div class="absolute inset-0 flex items-center justify-center">
            <div class="relative flex flex-col items-center gap-5 px-10 py-8 animate-[dropCardIn_220ms_cubic-bezier(0.2,0.8,0.2,1)_forwards]">
              <div
                class="absolute -inset-12 rounded-full bg-[radial-gradient(closest-side,var(--color-primary-200),transparent_70%)] blur-2xl"
                style="animation: dropHalo 2.4s ease-in-out infinite;"
              />
              <div class="relative">
                <div
                  class="absolute -inset-3 rounded-full border border-primary-300/50"
                  style="animation: dropOrbit 6s linear infinite;"
                />
                <div
                  class="relative w-20 h-20 rounded-full bg-white shadow-[0_8px_28px_-6px_rgba(79,70,229,0.35),0_2px_8px_-2px_rgba(15,23,42,0.08)] ring-1 ring-primary-100 flex items-center justify-center text-primary-500"
                  style="animation: dropGlyphFloat 2.4s ease-in-out infinite;"
                >
                  <Icon
                    size="1.5rem"
                    fill="currentColor"
                    src={async () =>
                      import("assets/img/svg/icons/paper-clip.svg?raw")
                    }
                  />
                </div>
              </div>
              <div class="relative flex flex-col items-center gap-1.5 text-center">
                <div class="text-[15px] font-semibold tracking-tight text-gray-900 font-['Inter']">
                  Drop to attach
                </div>
                <div class="text-xs text-gray-500 font-['Inter'] max-w-[280px] leading-relaxed">
                  Images, PDFs, or documents — Midas will read them with your next message.
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    };
  },
  { props: ["visible"] },
);

export const AIConversation = defineComponent(() => {
  const assistant = useAssistant();

  const scrollRef = ref();
  const dragDepth = ref<number>(0);
  const isDraggingFiles = computed(() => dragDepth.value > 0);

  const hasFileType = (e: DragEvent): boolean => {
    const types = e.dataTransfer?.types;
    if (!types) return false;
    for (let i = 0; i < types.length; i++) {
      if (types[i] === "Files") return true;
    }
    return false;
  };

  const onDragEnter = (e: DragEvent) => {
    if (!hasFileType(e)) return;
    e.preventDefault();
    dragDepth.value += 1;
  };

  const onDragOver = (e: DragEvent) => {
    if (!hasFileType(e)) return;
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "copy";
    }
  };

  const onDragLeave = (e: DragEvent) => {
    if (!hasFileType(e)) return;
    e.preventDefault();
    dragDepth.value = Math.max(0, dragDepth.value - 1);
  };

  const onDrop = (e: DragEvent) => {
    if (!hasFileType(e)) return;
    e.preventDefault();
    dragDepth.value = 0;
    const files = e.dataTransfer?.files;
    if (!files || files.length <= 0) return;
    const arr = Array.from(files);
    assistant.attachDroppedFiles(arr);
  };

  const scrollToLastMessage = () => {
    const el = unwrapElementRef(scrollRef);
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  assistant.events.subscribe(EAssistantEvent.INCOMING_MESSAGE_CHUNK, () => {
    setTimeout(() => {
      scrollToLastMessage();
    }, 300);
  });

  assistant.events.subscribe(EAssistantEvent.OUTGOING_MESSAGE, () => {
    setTimeout(() => {
      scrollToLastMessage();
    }, 300);
  });

  return () => {
    return (
      <div
        class="w-full h-full min-w-0 flex flex-col relative"
        onDragenter={onDragEnter}
        onDragover={onDragOver}
        onDragleave={onDragLeave}
        onDrop={onDrop}
      >
        <DropOverlay visible={isDraggingFiles.value} />
        <div class="w-full h-full min-w-0 flex flex-col pt-4 gap-0">
          <div
            class="w-full min-w-0 flex-[1px] overflow-y-auto overflow-x-hidden pb-6 pretty-scrollbar"
            ref={scrollRef}
          >
            {assistant.messages.value.length > 0 ? (
              <div class="w-full min-w-0 px-4 max-w-[800px] mx-auto">
                <MessageScreen assistant={assistant} />
              </div>
            ) : (
              <div class="w-full h-full min-w-0 px-4 max-w-[800px] mx-auto">
                <InitScreen assistant={assistant} />
              </div>
            )}
          </div>
          <div class="w-full flex-none relative">
            <div class="absolute inset-x-0 -top-8 h-8 bg-gradient-to-t from-gray-50 to-transparent pointer-events-none" />
            <div class="w-full px-4 max-w-[800px] mx-auto pb-4 pt-1">
              {assistant.messages.value.length <= 0 ? (
                <RecentChats />
              ) : (
                <div class="flex justify-center items-start gap-2 text-[10px] text-gray-400 font-normal font-['Inter'] mb-2.5">
                  <span class="text-gray-400 mt-[2px] shrink-0">
                    <Icon
                      fill="currentColor"
                      size="0.625rem"
                      src={async () =>
                        import("assets/img/svg/icons/info-circle.svg?raw")
                      }
                    />
                  </span>
                  <span>
                    Midas AI can make mistakes. Double-check important
                    information — nothing here is investment advice.
                  </span>
                </div>
              )}
              <InputArea assistant={assistant} />
            </div>
          </div>
        </div>
      </div>
    );
  };
});
